// Share row: plain intent links (no SDKs, no external scripts) — sharing obeys
// the telemetry rule: the game stays fully playable offline or ad-blocked, and
// a blocked popup just means the link opened nothing.
//
// VITE_GAME_URL pins the canonical page to share. It must be set for the
// itch.io build — the embed runs on a CDN domain, so the location fallback
// would share html-classic.itch.zone instead of the store page.

import { useState } from "react";
import { track } from "../telemetry.js";

const GAME_URL: string = import.meta.env.VITE_GAME_URL ?? `${location.origin}${location.pathname}`;

const DEFAULT_TEXT = "FIRST SILICON — found a fabless chip startup in 1993 and race the industry to first silicon.";

const NETWORKS: { id: string; label: string; href: (url: string, text: string) => string }[] = [
  { id: "x", label: "X", href: (u, t) => `https://x.com/intent/post?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}` },
  { id: "bluesky", label: "BLUESKY", href: (u, t) => `https://bsky.app/intent/compose?text=${encodeURIComponent(`${t} ${u}`)}` },
  { id: "reddit", label: "REDDIT", href: (u, t) => `https://www.reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}` },
  { id: "facebook", label: "FACEBOOK", href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: "linkedin", label: "LINKEDIN", href: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
  { id: "hn", label: "HN", href: (u, t) => `https://news.ycombinator.com/submitlink?u=${encodeURIComponent(u)}&t=${encodeURIComponent(t)}` },
];

/** `context` tags the telemetry (`title` / `game_over` / `system`); `text`
 *  overrides the pitch line — game over passes the run's outcome. */
export function ShareLinks({ context, text = DEFAULT_TEXT }: { context: string; text?: string }) {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    track("share", { network: "copy", context });
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    try {
      // navigator.clipboard is absent in non-secure contexts (same reality
      // telemetry's UUID fallback handles) — fall through to execCommand
      navigator.clipboard.writeText(GAME_URL).then(done, () => fallbackCopy(GAME_URL, done));
    } catch {
      fallbackCopy(GAME_URL, done);
    }
  };

  return (
    <div className="share-row">
      <span className="k">SHARE</span>
      {NETWORKS.map((n) => (
        <a
          key={n.id}
          href={n.href(GAME_URL, text)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("share", { network: n.id, context })}
        >
          {n.label}
        </a>
      ))}
      <button onClick={copyLink}>{copied ? "COPIED ✓" : "COPY LINK"}</button>
    </div>
  );
}

function fallbackCopy(text: string, done: () => void): void {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
    done();
  } catch {
    // clipboard unavailable: the visible links still work
  }
}
